"use client";

import { useEffect, useRef, useState } from "react";
import IHVDropdownProps from "./IHVDropdownProps";
import {
  Combobox,
  ComboboxItem,
  Group,
  Input,
  InputBase,
  Select,
  SelectProps,
  useCombobox,
} from "@mantine/core";
import style from "./HVDropdown.module.css";
import { IconCheck } from "@tabler/icons-react";
import HVText from "../HVText/HVText";
import scrollBarStyle from "./HVDropdpwnScroll.module.css";
import HVIcon from "../HVIcon/HVIcon";

type ExtendedComboboxItem = ComboboxItem & { count?: number };
type DropdownRawOption = string | ExtendedComboboxItem;

const renderSelectOption: SelectProps["renderOption"] = ({
  option,
  checked,
}) => {
  const item = option as ExtendedComboboxItem;
  return (
    <Group
      justify="space-between"
      className={`${style.optionContainer} ${checked ? style.selected : ""}`}
      w="100%"
    >
      <span>{item.label}</span>
      {item.count && <span className={style.count}>{item.count}</span>}
    </Group>
  );
};

const renderSelectOptionWithArrow: SelectProps["renderOption"] = ({
  option,
  checked,
}) => {
  const item = option as ExtendedComboboxItem;
  return (
    <Group justify="space-between" className={style.optionContainer} w="100%">
      <div className={style.groupItemWithArrow}>
        <span className={style.groupItemWithArrowemptySpace}>
          {checked ? (
            <HVIcon name="check" scale="large" theme="line" size="20" />
          ) : null}
        </span>
        <span>{item.label}</span>
      </div>
      {item.count !== undefined && (
        <span className={style.count}>{item.count}</span>
      )}
    </Group>
  );
};

const HVDropdown = ({
  type,
  selectedItem = null,
  options,
  selectWithArrow,
  selectWithSearch,
  selectWithCount,
  getSelectedValue,
  dropdownIconProps,
  dropdownOpenIconProps,
  dropdownCloseIconProps,
  ...dropdownContainerProps
}: IHVDropdownProps) => {
  const [opened, setOpened] = useState(false);
  const [rightSectionIcon, setRightSideIcon] = useState(
    <HVIcon
      {...dropdownIconProps}
      name="arrow_chevron_down"
      scale="small"
      theme="line"
      size="20"
      svgProps={{ stroke: "var(--hv-icon-normal-secondary)" }}
    />,
  );
  const [search, setSearch] = useState("");
  const [value, setValue] = useState<string | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [selectedComboItem, setSelectedComboItem] =
    useState<ExtendedComboboxItem>();
  const searchRef = useRef<HTMLInputElement>(null);
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
      setSearch("");
    },

    onDropdownOpen: () => {
      combobox.focusSearchInput();
    },
  });

  useEffect(() => {
    if (combobox.dropdownOpened) {
      setTimeout(() => {
        searchRef.current?.blur();
      }, 0);
    }
  }, [combobox.dropdownOpened]);

  useEffect(() => {
    setOptionToDropdown(selectedItem);
  }, []);

  const normalizedOptions: ExtendedComboboxItem[] = (
    options as DropdownRawOption[]
  ).map((item) =>
    typeof item === "string" ? { label: item, value: item } : item,
  );

  const setOptionToDropdown = (dropDownSelectedItem: string | null) => {
    if (!dropDownSelectedItem) return;
    const selectedItem = normalizedOptions.find(
      (item) => item.value === dropDownSelectedItem,
    );
    if (selectedItem) {
      setSelectedComboItem(selectedItem);
    }
  };

  const optionsForCombobox = normalizedOptions
    .filter((item) => {
      const optionItem = item as ExtendedComboboxItem;
      return optionItem.label
        .toLowerCase()
        .includes(search.toLowerCase().trim());
    })
    .map((item) => {
      const optionItem = item as ExtendedComboboxItem;
      return (
        <Combobox.Option
          value={optionItem.value}
          key={optionItem.value}
          className={`${style.dropdownOptionItem} ${
            selectedItem === optionItem.value &&
            !selectWithArrow &&
            style.selected
          }`}
          w="100%"
        >
          <div className={style.dropdownOptionContainer}>
            <div
              className={style.dropdownOptionItemWithCheckAndLabel}
              style={{
                gap: selectWithArrow ? "8px" : undefined,
              }}
            >
              <span
                className={style.dropdownOptionCheckItem}
                style={{
                  width: selectWithArrow ? "20px" : undefined,
                }}
              >
                {selectWithArrow && selectedItem === optionItem.value ? (
                  <HVIcon name="check" scale="large" theme="line" size="20" />
                ) : null}
              </span>
              <span>{optionItem.label}</span>
            </div>

            {optionItem.count !== undefined && (
              <span className={style.count}>{optionItem.count}</span>
            )}
          </div>
        </Combobox.Option>
      );
    });

  return (
    <>
      {selectWithSearch || selectWithCount ? (
        <div className={style.comboboxWrapper}>
          <Combobox
            store={combobox}
            withinPortal={false}
            onOptionSubmit={(val) => {
              setValue(val);
              getSelectedValue(val);
              setOptionToDropdown(val);
              combobox.closeDropdown();
            }}
          >
            <Combobox.Target>
              <InputBase
                component="button"
                type="button"
                pointer
                rightSection={
                  combobox.dropdownOpened ? (
                    <HVIcon
                      name="arrow_chevron_up"
                      scale="small"
                      theme="line"
                    />
                  ) : (
                    <HVIcon
                      name="arrow_chevron_down"
                      scale="small"
                      theme="line"
                      svgProps={{ stroke: "var(--hv-icon-normal-secondary)" }}
                    />
                  )
                }
                onClick={() => combobox.toggleDropdown()}
                rightSectionPointerEvents="none"
                className={style.dropdownItemContainer}
                styles={{
                  input: combobox.dropdownOpened
                    ? { border: "1px solid var(--hv-input-outline-active)" }
                    : undefined,
                }}
              >
                {selectedItem !== null ? (
                  <Group justify="space-between" w="100%">
                    <span>{selectedComboItem?.label}</span>
                    {selectedComboItem?.count && (
                      <span className={style.count}>
                        {selectedComboItem?.count}
                      </span>
                    )}
                  </Group>
                ) : (
                  value || (
                    <Input.Placeholder
                      style={{ color: "var(--hv-text-normal-secondary)" }}
                    >
                      Choose an option
                    </Input.Placeholder>
                  )
                )}
              </InputBase>
            </Combobox.Target>

            <Combobox.Dropdown className={style.comboboxOptionsContainer}>
              {selectWithSearch && (
                <Combobox.Search
                  ref={searchRef}
                  value={search}
                  onChange={(event) => setSearch(event.currentTarget.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  placeholder="Search input field"
                  className={style.search}
                  leftSection={
                    <span className={style.leftIconWrapper}>
                      <HVIcon
                        name="search"
                        scale="large"
                        theme="line"
                        size="20"
                        svgProps={{ stroke: "var(--hv-icon-normal-secondary)" }}
                      />
                    </span>
                  }
                  rightSection={
                    searchFocused ? (
                      <span
                        className={style.rightIconWrapper}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setSearch("");
                        }}
                      >
                        <HVIcon
                          name="fail"
                          scale="large"
                          theme="fill"
                          size="20"
                          svgProps={{ fill: "var(--hv-icon-normal-primary)" }}
                          className={style.clearSearch}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setSearch("");
                          }}
                        />
                      </span>
                    ) : null
                  }
                  rightSectionPointerEvents="auto"
                />
              )}
              <Combobox.Options className={style.dropdownOptions}>
                {optionsForCombobox.length > 0 ? (
                  optionsForCombobox
                ) : (
                  <Combobox.Empty className={style.empty}>
                    No data found
                  </Combobox.Empty>
                )}
              </Combobox.Options>
            </Combobox.Dropdown>
          </Combobox>
        </div>
      ) : (
        <Select
          data={options}
          value={selectedItem}
          withCheckIcon={false}
          classNames={{
            root:
              type === "medium"
                ? style.rootWrapperMedium
                : style.rootWrapperLarge,
            input: style.dropdownContainer,
            section: style.sectionContainer,
            dropdown: style.optionsContainer,
            option: style.option,
          }}
          size={type === "medium" ? "xs" : "sm"}
          styles={{
            input: {
              height: type === "medium" ? "24px" : "32px",
              backgroundColor: opened
                ? "var(--hv-input-foreground-default)"
                : "",
              color: opened ? "var(--hv-text-normal-primary)" : "",
              border: opened ? "1px solid var(--hv-input-outline-active)" : "",
              fontSize:
                type === "medium"
                  ? "var(--hv-fontsize-body-xs)"
                  : "var(--hv-fontsize-body-sm)",
            },
          }}
          onDropdownOpen={() => {
            setOpened(true);
            setRightSideIcon(
              <HVIcon
                {...dropdownOpenIconProps}
                name="arrow_chevron_up"
                scale="small"
                theme="line"
                size="20"
                svgProps={{ stroke: "var(--hv-icon-normal-secondary)" }}
              />,
            );
          }}
          onDropdownClose={() => {
            setOpened(false);
            setRightSideIcon(
              <HVIcon
                {...dropdownCloseIconProps}
                name="arrow_chevron_down"
                scale="small"
                theme="line"
                size="20"
                svgProps={{ stroke: "var(--hv-icon-normal-secondary)" }}
              />,
            );
          }}
          comboboxProps={{ offset: 4 }}
          renderOption={
            selectWithArrow ? renderSelectOptionWithArrow : renderSelectOption
          }
          scrollAreaProps={{ classNames: scrollBarStyle }}
          rightSection={rightSectionIcon}
          onChange={(option) => getSelectedValue && getSelectedValue(option)}
          {...dropdownContainerProps}
        />
      )}
    </>
  );
};

export default HVDropdown;
